import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { BaseTable } from '@/components';
import { useBeltMisalignmentTable } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/useBeltMisalignmentTable';
import { SectionEmptyState } from '@/components/pages/AssetsPreviewPage/AssetsOverview/EmptyStates';
import { NotificationPreferencesTableRef } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/NotificationPreferences';
import {
  groupAssetsBySite,
  updateNotificationPreferencesCache,
} from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/utils';
import { isEqual } from 'lodash';
import { NOTIFICATION_PREFERENCES_TABLE_CLASS_NAMES } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/const';
import { useNestedParameterUpdate } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/useNestedParameterUpdate';
import { dispatch } from '@/store/store';

interface BeltMisalignmentTableProps {
  data: any;
  setIsDirty: any;
  updateNotificationPreferences: any;
}

export const BeltMisalignmentTable = forwardRef<NotificationPreferencesTableRef, BeltMisalignmentTableProps>(
  ({ data, setIsDirty, updateNotificationPreferences }: BeltMisalignmentTableProps, ref) => {
    const { columns } = useBeltMisalignmentTable();
    const [currentData, setCurrentData] = useState<any>(data);
    const updateNestedParameter = useNestedParameterUpdate(setCurrentData);

    useEffect(() => {
      setIsDirty(!isEqual(currentData, data));
    }, [currentData, data]);

    useImperativeHandle(ref, () => ({
      triggerSave: async (callback?: () => void): Promise<void> => {
        setIsDirty(false);

        // Manually update notification preferences rock size cache data
        dispatch(updateNotificationPreferencesCache('belt_misalignment', currentData));

        const payload = groupAssetsBySite(currentData);
        await updateNotificationPreferences(payload);

        callback?.();
      },
    }));

    if (!data?.length) {
      return <SectionEmptyState text="No assets were found based on the selected filters." />;
    }

    return (
      <BaseTable
        columns={columns}
        data={currentData}
        wrapHeaders={false}
        meta={{ updateData: updateNestedParameter }}
        classNames={NOTIFICATION_PREFERENCES_TABLE_CLASS_NAMES}
      />
    );
  }
);

BeltMisalignmentTable.displayName = 'BeltMisalignmentTable';
